import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ListenButton from "../../components/ListenButton";
import GameShell, { shuffle } from "./GameShell";

export default function LetterSoundGame() {
    const [pool, setPool] = useState([]);
    const [round, setRound] = useState(null);
    const [correct, setCorrect] = useState(0);
    const [total, setTotal] = useState(0);
    const [picked, setPicked] = useState(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/games/letter-sounds").then((r) => {
            setPool(r.data);
            next(r.data);
        });
    }, []);

    const next = (data = pool) => {
        if (total >= 10 && data === pool) {
            setDone(true);
            return;
        }
        const items = shuffle(data);
        const target = items[0];
        const decoys = items.slice(1, 4);
        const options = shuffle([target, ...decoys]);
        setRound({ target, options });
        setPicked(null);
    };

    const pick = (opt) => {
        if (picked) return;
        setPicked(opt);
        const ok = opt.letter === round.target.letter;
        setTotal((t) => t + 1);
        if (ok) {
            setCorrect((c) => c + 1);
            toast.success(`Correct! "${opt.letter}" says ${opt.sound}`);
        } else {
            toast.error(`Not quite. "${round.target.letter}" says ${round.target.sound}`);
        }
        setTimeout(() => next(), 900);
    };

    const finish = async () => {
        setDone(true);
        try {
            await api.post("/progress/game-score", { game: "letter-sound", score: correct * 5, correct, total });
            toast.success(`+${correct * 5} XP earned`);
        } catch {}
    };

    useEffect(() => {
        if (total >= 10 && !done) finish();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);

    const reset = () => {
        setCorrect(0); setTotal(0); setDone(false); next(pool);
    };

    if (!round) return <GameShell title="Letter & Sound"><p>Loading…</p></GameShell>;

    return (
        <GameShell
            title="Letter & Sound"
            intro="Listen to the sound, then tap the letter that makes it."
            testId="letter-sound-game"
            instructions="This is the Letter and Sound game. Tap Hear the sound to play a phoneme. Then tap the letter from the three choices below that makes that sound."
        >
            <div className="flex items-center justify-between mb-8">
                <p className="text-sm text-[#819888]">Round {total + (done ? 0 : 1)} of 10</p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="game-score">Score: {correct} / {total}</p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Round complete</h2>
                    <p className="text-lg text-[#515B57] mb-6">You got {correct} out of {total} correct.</p>
                    <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]" data-testid="game-replay">
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    <div className="card-rw p-10 mb-8 text-center">
                        <p className="label-eyebrow mb-4">The sound is</p>
                        <p className="text-5xl font-semibold mb-6 text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{round.target.sound}</p>
                        <ListenButton
                            text={round.target.letter}
                            mode="phoneme"
                            size="lg"
                            label="Hear the sound"
                            testId="ls-listen"
                        />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="ls-options">
                        {round.options.map((opt) => {
                            const isPicked = picked?.letter === opt.letter;
                            const isCorrect = picked && opt.letter === round.target.letter;
                            const cls = isCorrect ? "tile tile-correct" : isPicked ? "tile tile-wrong" : "tile";
                            return (
                                <button
                                    key={opt.letter}
                                    onClick={() => pick(opt)}
                                    className={`${cls} text-5xl py-10`}
                                    data-testid={`ls-option-${opt.letter}`}
                                    disabled={!!picked}
                                >
                                    {opt.letter}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </GameShell>
    );
}
